import { AgGrid, Flex, HelpIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { RiskRating } from "~/graphql/supergraphEnums";
import PositionNameRenderer from "../../CompliancePositionsTable/PositionNameRenderer";
import ProgramRenderer from "../../CompliancePositionsTable/ProgramRenderer";
import { ProgramColumn } from "../../CompliancePositionsTable/styles_vanilla.css";
import { RiskRatingRenderer } from "../../riskRating/RiskRatingRenderer";
import { getRiskRatingCellStyle } from "../../riskRating/RiskRatingRenderer/utils";
import { calculateRiskRatingChange, sortRiskRating } from "../../riskRating/utils";
import { AffectedProfileRenderer } from "./AffectedProfileRenderer";
import { notApplicableContainer } from "./styles_vanilla.css";
import { PositionRowItem } from "./types";
import { PositionsGridContext } from "./index";

export const positionColumnDefinition: AgGrid.ColDef[] = [
  {
    field: "position",
    pinned: "left",
    headerName: "Position",
    cellRenderer: PositionNameRenderer,
    minWidth: 512,
    cellRendererParams: ({ data }: { data: PositionRowItem }) => ({
      account: data.position?.accountName,
      entity: data.position?.entityName,
      positionId: data.positionId,
      arenaId: data.arenaId,
      entityId: data.entityId,
    }),
  },
  {
    field: "program",
    headerName: "Program",
    cellRenderer: ProgramRenderer,
    cellRendererParams: ({ data }: { data: PositionRowItem }) => ({
      value: [data.program],
    }),
    cellClass: ProgramColumn,
  },
  {
    field: "affectedProfile",
    headerName: "Affected Profile",
    minWidth: 200,
    cellRenderer: AffectedProfileRenderer,
    cellRendererParams: ({ data }: { data: PositionRowItem }) => ({
      value: data.affectedProfiles,
    }),
  },
  {
    field: "client",
    headerName: "Client",
    valueGetter: ({ data }: { data: PositionRowItem }) => {
      return data.client ?? "";
    },
  },
  {
    field: "riskRating",
    headerName: "Updated risk rating",
    comparator: sortRiskRating,
    cellClass: (params: AgGrid.CellClassParams<PositionRowItem>) => {
      return getRiskRatingCellStyle(params.data?.riskRatingInfo?.proposedRating);
    },
    cellRenderer: (
      params: AgGrid.ICellRendererParams<PositionRowItem, unknown, PositionsGridContext>,
    ) => {
      const showRiskRating = params?.data?.program?.toUpperCase().startsWith("AML");
      const riskRatingInfo = params?.data?.riskRatingInfo;

      const handleInspectRiskRating = () => {
        params.context?.onInspectRiskRating(params?.data ?? null);
      };

      if (showRiskRating) {
        const ratingAvailable = riskRatingInfo?.proposedRating != RiskRating.None;
        const changeIcon = calculateRiskRatingChange(
          riskRatingInfo?.currentRating,
          riskRatingInfo?.proposedRating,
        );
        return (
          <RiskRatingRenderer
            riskRating={riskRatingInfo?.proposedRating}
            changeIcon={ratingAvailable ? changeIcon : null}
            onInspectRiskRating={ratingAvailable ? handleInspectRiskRating : undefined}
            showHoverInspector={ratingAvailable}
            isFullWidth={true}
          />
        );
      }
      return (
        <HelpIndicator
          id="risk-rating-not-applicable"
          placement="top"
          content="This position is in a compliance program that doesn't report risk ratings"
        >
          <Flex className={notApplicableContainer}>
            <Icon type={"circle"} variant="outline" color={Colors.Neutral600} />
            <Text variant="regular-md">Not applicable</Text>
          </Flex>
        </HelpIndicator>
      );
    },
  },
];
